/**
 * H1 «Личное дело»: внешность, имущество, родители и возраст детей.
 *
 * Внешность — чистая косметика: генерируется при рождении от seed и региона
 * ВОЗ (`generateLook`), рост растёт до ADULT_AGE, после GREY_HAIR_MIN_AGE
 * волосы седеют с вероятностью GREY_HAIR_P в год (`growLook`). Ни одно из
 * этих полей не влияет на смертность, деньги или события — калибровка
 * этапа 1 не зависит от них (все жребии — в собственных `fork`).
 *
 * Жильё (`assets.housing`): `family` с рождения, `rent` при первой работе
 * или переезде (смена страны/города), `own` после HOUSING_OWN_MIN_AGE при
 * деньгах ≥ HOUSING_OWN_MONEY_GDP·ВВП с вероятностью HOUSING_OWN_P в год.
 * Покупка денег не списывает (имущество — запись в досье, не экономика).
 * Машина — есть права (`flags.license`) и деньги ≥ CAR_MONEY_GDP·ВВП.
 *
 * `ensureLegacyFields` дополняет сохранения до этого этапа: недостающие
 * поля выводятся из флагов (`parentsDead`, `jobStartAge`, `license`) и
 * детерминированно от `createRng(seed).fork('legacy')`; энергия года —
 * `energyFor` от восстановленной жизни, визиты — пустые.
 */
import { getCountry, getValue, type Dataset, type WhoRegion } from '@life/data';
import { energyFor } from './energy';
import { FLAG_LICENSE, FLAG_PARENTS_DEAD } from './events/defs';
import { createRng, type Rng } from './rng';
import type { Life, Sex } from './types';

export type Build = 'slim' | 'average' | 'stout';

export interface Look {
  heightCm: number;
  build: Build;
  /** Ключи i18n: `look.hair.*`, `look.eyes.*`, `look.mark.*`. */
  hairKey: string;
  eyesKey: string;
  markKey: string;
}

export type Housing = 'family' | 'rent' | 'own';

export interface Assets {
  housing: Housing;
  car: boolean;
}

/** Жизнь из сохранения до этапа 2d: новые поля могут отсутствовать. */
type LegacyField = 'look' | 'assets' | 'parentsAlive' | 'childrenAges' | 'energy' | 'visits' | 'dream' | 'pendingDream' | 'yearGoal' | 'milestones';
export type LegacyLife = Omit<Life, LegacyField> & Partial<Pick<Life, LegacyField>>;

export const ADULT_AGE = 18;
export const GREY_HAIR_MIN_AGE = 50;
export const GREY_HAIR_P = 0.15;
export const HAIR_GREY_KEY = 'look.hair.grey';
export const HAIR_BALD_KEY = 'look.hair.bald';
export const HOUSING_OWN_MIN_AGE = 30;
export const HOUSING_OWN_MONEY_GDP = 3;
export const HOUSING_OWN_P = 0.2;
export const CAR_MONEY_GDP = 0.5;
/** Запись журнала о своём жилье. */
export const HOUSING_OWN_KEY = 'assets.housing.own';
/** Родителей у жизни двое. */
export const PARENTS = 2;
/** Вторая потеря родителя идёт молча (без записи), с тем же ростом веса по возрасту, что и событие. */
const SECOND_PARENT_MIN_AGE = 15;
const SECOND_PARENT_RAMP_AGE = 30;
const SECOND_PARENT_MAX_P = 0.12;

const NEWBORN_HEIGHT = 50;
const NEWBORN_SD = 2;
/** Средний рост взрослого мужчины по региону ВОЗ, см (женщины — минус 12). */
const ADULT_HEIGHT_M: Readonly<Record<WhoRegion, number>> = {
  AFR: 168,
  AMR: 173,
  SEAR: 165,
  EUR: 178,
  EMR: 171,
  WPR: 171,
};
const SEX_HEIGHT_GAP = 12;
const ADULT_HEIGHT_SD = 6;

const BUILDS: readonly Build[] = ['slim', 'average', 'stout'];
const BUILD_WEIGHTS = [0.3, 0.5, 0.2];

const HAIR_KEYS: Readonly<Record<WhoRegion, readonly string[]>> = {
  AFR: ['look.hair.black', 'look.hair.black', 'look.hair.dark'],
  AMR: ['look.hair.dark', 'look.hair.black', 'look.hair.brown', 'look.hair.fair'],
  SEAR: ['look.hair.black', 'look.hair.black', 'look.hair.dark'],
  EUR: ['look.hair.brown', 'look.hair.dark', 'look.hair.fair', 'look.hair.red'],
  EMR: ['look.hair.black', 'look.hair.dark', 'look.hair.brown'],
  WPR: ['look.hair.black', 'look.hair.black', 'look.hair.dark'],
};
const EYES_KEYS: Readonly<Record<WhoRegion, readonly string[]>> = {
  AFR: ['look.eyes.brown', 'look.eyes.brown', 'look.eyes.black'],
  AMR: ['look.eyes.brown', 'look.eyes.brown', 'look.eyes.green', 'look.eyes.blue'],
  SEAR: ['look.eyes.brown', 'look.eyes.black'],
  EUR: ['look.eyes.blue', 'look.eyes.grey', 'look.eyes.brown', 'look.eyes.green'],
  EMR: ['look.eyes.brown', 'look.eyes.black', 'look.eyes.green'],
  WPR: ['look.eyes.brown', 'look.eyes.black'],
};
const MARK_KEYS: readonly string[] = ['look.mark.none', 'look.mark.none', 'look.mark.mole', 'look.mark.freckles', 'look.mark.scar', 'look.mark.dimples', 'look.mark.gap'];

/** Регион без датасета (старые сохранения в тестах): европейские средние. */
const DEFAULT_REGION: WhoRegion = 'EUR';

/** Доля взрослого роста, достигнутая к возрасту `age` (0 → новорождённый, ≥ ADULT_AGE → 1). */
function growthShare(age: number): number {
  if (age <= 0) return 0;
  if (age >= ADULT_AGE) return 1;
  // Быстрый рост в первые годы, плато к 18: вогнутая кривая без скачков.
  const x = age / ADULT_AGE;
  return Math.sqrt(x) * (0.6 + 0.4 * x);
}

function adultHeightMean(sex: Sex, region: WhoRegion): number {
  return ADULT_HEIGHT_M[region] - (sex === 'f' ? SEX_HEIGHT_GAP : 0);
}

/** Рост на возраст `age` по росту при рождении и взрослому росту. */
function heightAt(age: number, newborn: number, adult: number): number {
  return Math.round(newborn + (adult - newborn) * growthShare(age));
}

/** Внешность новорождённого: все жребии из `rng`. */
export function generateLook(sex: Sex, region: WhoRegion, rng: Rng): Look {
  const heightCm = Math.round(rng.fork('height').normal(NEWBORN_HEIGHT, NEWBORN_SD));
  return {
    heightCm: Math.max(40, Math.min(60, heightCm)),
    build: rng.fork('build').weighted(BUILDS, BUILD_WEIGHTS),
    hairKey: rng.fork('hair').pick(HAIR_KEYS[region]),
    eyesKey: rng.fork('eyes').pick(EYES_KEYS[region]),
    markKey: rng.fork('mark').pick(MARK_KEYS),
  };
}

/** Взрослый рост этой жизни: детерминирован от seed, не хранится в состоянии. */
function adultHeightFor(life: Pick<Life, 'seed' | 'sex'>, region: WhoRegion): number {
  const mean = adultHeightMean(life.sex, region);
  return Math.round(mean + createRng(life.seed).fork('look/adult').normal(0, ADULT_HEIGHT_SD));
}

/**
 * Год внешности: рост до ADULT_AGE (к возрасту `life.age + 1`), седина после
 * GREY_HAIR_MIN_AGE с вероятностью GREY_HAIR_P. Лысина и седина не меняются.
 */
export function growLook(life: Life, region: WhoRegion, rng: Rng): Look {
  let look = life.look;
  const nextAge = life.age + 1;
  if (nextAge <= ADULT_AGE) {
    const adult = adultHeightFor(life, region);
    const grown = heightAt(nextAge, NEWBORN_HEIGHT, adult);
    // Рост не убывает: если взрослый рост ниже текущего (крайний seed), стоим на месте.
    look = { ...look, heightCm: Math.max(look.heightCm, grown, look.heightCm + 1) };
  }
  if (nextAge > GREY_HAIR_MIN_AGE && look.hairKey !== HAIR_GREY_KEY && look.hairKey !== HAIR_BALD_KEY && rng.chance(GREY_HAIR_P)) {
    look = { ...look, hairKey: HAIR_GREY_KEY };
  }
  return look;
}

/**
 * Год имущества. `moved` — страна или город изменились за этот год.
 * Возвращает новые assets и флаг «куплено жильё» для записи в журнал.
 */
export function updateAssets(ds: Dataset, life: Life, moved: boolean, rng: Rng): { assets: Assets; boughtHome: boolean } {
  let { housing } = life.assets;
  let boughtHome = false;
  const gdp = Math.max(0, getValue(ds, life.iso3, 'gdpPpp', life.birthYear + life.age).v);
  if (housing === 'family' && (life.job !== null || moved)) housing = 'rent';
  if (housing !== 'own' && life.age >= HOUSING_OWN_MIN_AGE && gdp > 0 && life.money >= HOUSING_OWN_MONEY_GDP * gdp && rng.chance(HOUSING_OWN_P)) {
    housing = 'own';
    boughtHome = true;
  }
  const car = life.assets.car || (life.flags[FLAG_LICENSE] === true && gdp > 0 && life.money >= CAR_MONEY_GDP * gdp);
  return { assets: { housing, car }, boughtHome };
}

/** Вторая потеря родителя: молча, без записи; parentsAlive ниже нуля не уходит. */
export function loseSecondParent(life: Life, rng: Rng): Life {
  if (life.parentsAlive !== 1 || life.age < SECOND_PARENT_MIN_AGE) return life;
  const p = Math.min(SECOND_PARENT_MAX_P, 0.01 + Math.max(0, life.age - SECOND_PARENT_RAMP_AGE) * 0.004);
  if (!rng.chance(p)) return life;
  return { ...life, parentsAlive: 0, flags: { ...life.flags, [FLAG_PARENTS_DEAD]: PARENTS } };
}

/** Возраст детей за год: те, кто был в начале года (`atStart`), становятся старше на 1; новорождённые остаются 0. */
export function ageChildren(life: Life, atStart: number): Life {
  const ages = life.childrenAges.map((a, i) => (i < atStart ? a + 1 : a));
  // Страховка от рассинхрона с `children` (старые пути без push): дорисовываем нули.
  while (ages.length < life.children) ages.push(0);
  return { ...life, childrenAges: ages };
}

function regionOf(ds: Dataset | undefined, iso3: string): WhoRegion {
  if (!ds) return DEFAULT_REGION;
  try {
    return getCountry(ds, iso3).whoRegion;
  } catch {
    return DEFAULT_REGION;
  }
}

/** Дополняет жизнь из старого сохранения недостающими полями H1; заполненную возвращает как есть. */
export function ensureLegacyFields(life: LegacyLife, ds?: Dataset): Life {
  if (
    life.look &&
    life.assets &&
    typeof life.parentsAlive === 'number' &&
    Array.isArray(life.childrenAges) &&
    typeof life.energy === 'number' &&
    life.visits !== undefined &&
    life.dream !== undefined &&
    life.pendingDream !== undefined &&
    life.yearGoal !== undefined &&
    Array.isArray(life.milestones)
  ) {
    return life as Life;
  }
  const rng = createRng(life.seed).fork('legacy');
  const region = regionOf(ds, life.iso3);

  let look = life.look;
  if (!look) {
    const born = generateLook(life.sex, region, rng.fork('look'));
    const adult = adultHeightFor(life, region);
    const heightCm = heightAt(life.age, born.heightCm, adult);
    const grey = life.age > GREY_HAIR_MIN_AGE && rng.fork('grey').chance(1 - Math.pow(1 - GREY_HAIR_P, life.age - GREY_HAIR_MIN_AGE));
    look = { ...born, heightCm, hairKey: grey ? HAIR_GREY_KEY : born.hairKey };
  }

  const dead = Math.min(PARENTS, Math.max(0, Math.trunc(Number(life.flags[FLAG_PARENTS_DEAD] ?? 0)) || 0));
  const parentsAlive = typeof life.parentsAlive === 'number' ? life.parentsAlive : PARENTS - dead;

  let childrenAges = life.childrenAges;
  if (!Array.isArray(childrenAges)) {
    const agesRng = rng.fork('children');
    const n = Math.max(0, Math.trunc(life.children));
    const maxAge = Math.max(0, life.age - 1);
    childrenAges = [];
    for (let i = 0; i < n; i += 1) childrenAges.push(Math.min(maxAge, agesRng.int(0, Math.min(maxAge, 25))));
    childrenAges.sort((a, b) => b - a);
  }

  const assets: Assets = life.assets ?? {
    housing: life.job !== null || life.flags['jobStartAge'] !== undefined ? 'rent' : 'family',
    car: false,
  };

  const visits = life.visits ?? {};
  const filled: Life = {
    ...life,
    look,
    assets,
    parentsAlive,
    childrenAges,
    visits,
    energy: life.energy ?? 0,
    dream: life.dream ?? null,
    pendingDream: life.pendingDream ?? null,
    yearGoal: life.yearGoal ?? null,
    milestones: Array.isArray(life.milestones) ? life.milestones : [],
  };
  return typeof life.energy === 'number' ? filled : { ...filled, energy: energyFor(filled) };
}
